import { apiClient } from "@/services/api/axiosClient";
import type { ApiResponse } from "@/services/api/api.types";
import { API_ENDPOINTS } from "@/services/api/endpoints";

export { couponService } from "./coupon.service";
export type { ApplyCouponCodePayload, AppliedCouponBreakdown } from "./coupon.service";

/* -------------------------------------------------------------------------- */
/*                                    TYPES                                   */
/* -------------------------------------------------------------------------- */

export type CreatePaymentIntentPayload = {
  user_id: number | string;
  property_id: string;
  year: number;
  amount: number;
  currency: string;
  use_wallet: boolean;
  /** Applied promo code, or "" when none. */
  coupon_code: string;
};

export type CreateMultiPaymentIntentPayload = {
  // amount: number;
  currency: string;
  price_id: string;
  property_ids: string[];
  user_id: number | string | null;
  year: number;
  use_wallet: boolean;
  coupon_code: string;
  success_url?: string;
  cancel_url?: string;
};

export type CreatePaymentIntentData = {
  payment_id: number;
  payment_intent_id: string;
  client_secret: string;
  amount: number;
  currency: string;
  status: string;
  subscription_id: string | null;
  property_id: string;
  user_id: number;
  year: number;
  created_at: string;
};

export type CreatePaymentIntentResponse = {
  status: string;
  message: string;
  data?: CreatePaymentIntentData;
  client_secret: string;
  payment_intent_id: string;
  amount: number;
  currency: string;
  subscription_id: string | null;
  property_id: string;
  user_id: number;
  year: number;
  created_at: string;
};

export type UpdatePaymentStatusPayload = {
  payment_intent_id: string;
};

export type UpdatePaymentStatusResponse = {
  status: string;
  message: string;
  data?: CreatePaymentIntentData;
};

export type PaymentCheckoutStatusItem = {
  payment_id: number;
  property_id: string;
  user_id: number | null;
  email: string | null;
  year: number;
  amount: number;
  final_amount: number;
  paid: boolean;
  status: string;
  paid_at: string | null;
  subscription_id: string | null;
  stripe_checkout_session_id: string;
};

export type CheckoutStatusData = {
  checkout_session_id: string;
  checkout_status: string;
  is_complete: boolean;
  paid_count: number;
  total_count: number;
  customer_email?: string;
  payments: PaymentCheckoutStatusItem[];
};

export type CheckoutStatusResponse = {
  status: string;
  message: string;
  data: CheckoutStatusData;
  error?: string | null;
};

export type SaveProtestDocumentsPayload = {
  user_id: number | string;
  property_id: string;
  property_type: "residential" | "commercial";
  documents: {
    document_type: string;
    document_url: string;
  }[];
  property_details?: {
    name?: string;
    address?: string;
    street?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
};

export type SaveProtestDocumentsResponse = {
  status: string;
  message: string;
  data?: {
    protest_id: string;
    success: boolean;
  };
};

/* -------------------------------------------------------------------------- */
/*                                  SERVICES                                  */
/* -------------------------------------------------------------------------- */

export const protestDocumentService = {
  /**
   * Save multiple signed protest documents to the API
   */
  async saveProtestDocuments(
    payload: SaveProtestDocumentsPayload
  ): Promise<ApiResponse<SaveProtestDocumentsResponse>> {
    const { data } = await apiClient.post<ApiResponse<SaveProtestDocumentsResponse>>(
      API_ENDPOINTS.PROPERTY.PROTEST_SAVE_DOCUMENTS,
      payload
    );
    return data;
  },
};

export const paymentSubscriptionService = {
  /**
   * Create Stripe payment intent for property protest payment
   */
  async createPaymentIntent(
    payload: CreatePaymentIntentPayload
  ): Promise<ApiResponse<CreatePaymentIntentResponse>> {
    const { data } = await apiClient.post<ApiResponse<CreatePaymentIntentResponse>>(
      API_ENDPOINTS.PROPERTY.PAYMENT_CREATE_INTENT,
      payload
    );
    return data;
  },

  /**
   * Create Stripe multi-property payment intent
   */
  async createMultiPaymentIntent(
    payload: CreateMultiPaymentIntentPayload
  ): Promise<ApiResponse<CreatePaymentIntentResponse>> {
    const { data } = await apiClient.post<ApiResponse<CreatePaymentIntentResponse>>(
      API_ENDPOINTS.PROPERTY.PAYMENT_CREATE_MULTI_INTENT,
      payload
    );
    return data;
  },

  /**
   * Update payment status after Stripe payment succeeds
   */
  async updatePaymentStatus(
    payload: UpdatePaymentStatusPayload
  ): Promise<ApiResponse<UpdatePaymentStatusResponse>> {
    const { data } = await apiClient.post<ApiResponse<UpdatePaymentStatusResponse>>(
      API_ENDPOINTS.PROPERTY.PAYMENT_UPDATE_STATUS,
      payload
    );
    return data;
  },

  /**
   * Get payment checkout status by Stripe session_id
   */
  async getCheckoutStatus(sessionId: string): Promise<ApiResponse<CheckoutStatusResponse>> {
    const { data } = await apiClient.get<ApiResponse<CheckoutStatusResponse>>(
      API_ENDPOINTS.PROPERTY.PAYMENT_CHECKOUT_STATUS(sessionId)
    );
    return data;
  },
};
